import { supabase } from '../lib/supabase'
import type { Database } from '../types/database'

export type Category = Database['public']['Tables']['categories']['Row']
export type CategoryInsert = Database['public']['Tables']['categories']['Insert']
export type CategoryUpdate = Database['public']['Tables']['categories']['Update']

export interface CategoryWithVideoCount extends Category {
  video_count: number
}

export interface CategoryWithTags extends Category {
  tags: Array<{
    id: string
    name: string
    slug: string
    is_active: boolean
  }>
}

export class CategoryService {
  // Get all categories with their tags
  static async getCategoriesWithTags(): Promise<CategoryWithTags[]> {
    // First get all active categories
    const { data: categories, error: categoriesError } = await supabase
      .from('categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true })

    if (categoriesError) throw categoriesError

    // Get all active tags with their category info
    const { data: tags, error: tagsError } = await supabase
      .from('tags')
      .select('id, name, slug, is_active, category_id')
      .eq('is_active', true)

    if (tagsError) throw tagsError

    // Group tags by category
    const result: CategoryWithTags[] = (categories || []).map(category => ({
      ...category,
      tags: (tags || [])
        .filter(tag => tag.category_id === category.id)
        .map(tag => ({
          id: tag.id,
          name: tag.name,
          slug: tag.slug,
          is_active: tag.is_active
        }))
    }))

    return result
  }

  // Get all categories with optional video counts
  static async getCategories(includeVideoCount = false): Promise<CategoryWithVideoCount[]> {
    const query = supabase
      .from('categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true })

    const { data: categories, error } = await query

    if (error) throw error

    if (!includeVideoCount) {
      return (categories || []).map(cat => ({ ...cat, video_count: 0 }))
    }

    // Get video counts for each category
    // Note: Assumes video.category contains category slug values
    const { data: videos, error: videoError } = await supabase
      .from('videos')
      .select('category')

    if (videoError) throw videoError

    const videoCounts = (videos || []).reduce((acc, video) => {
      acc[video.category] = (acc[video.category] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return (categories || []).map(cat => ({
      ...cat,
      video_count: videoCounts[cat.slug] || 0
    }))
  }

  // Get all categories for admin (including inactive)
  static async getAllCategories(includeVideoCount = false): Promise<CategoryWithVideoCount[]> {
    const query = supabase
      .from('categories')
      .select('*')
      .order('display_order', { ascending: true })

    const { data: categories, error } = await query

    if (error) throw error

    if (!includeVideoCount) {
      return (categories || []).map(cat => ({ ...cat, video_count: 0 }))
    }

    // Get video counts for each category
    // Note: Assumes video.category contains category slug values
    const { data: videos, error: videoError } = await supabase
      .from('videos')
      .select('category')

    if (videoError) throw videoError

    const videoCounts = (videos || []).reduce((acc, video) => {
      acc[video.category] = (acc[video.category] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return (categories || []).map(cat => ({
      ...cat,
      video_count: videoCounts[cat.slug] || 0
    }))
  }

  // Get a single category by ID
  static async getCategory(id: string): Promise<Category | null> {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('id', id)
      .maybeSingle()

    if (error) throw error
    return data
  }

  // Get a single category by slug
  static async getCategoryBySlug(slug: string): Promise<Category | null> {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('slug', slug)
      .maybeSingle()

    if (error) throw error
    return data
  }

  // Create new category
  static async createCategory(category: CategoryInsert): Promise<Category> {
    // Check if slug already exists
    const existing = await this.getCategoryBySlug(category.slug)
    if (existing) {
      throw new Error('このスラッグは既に使用されています')
    }

    // If no display_order provided, set it to the next available number
    if (!category.display_order) {
      const { data: maxOrder } = await supabase
        .from('categories')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1)
        .maybeSingle()

      category.display_order = (maxOrder?.display_order || 0) + 1
    }

    const { data, error } = await supabase
      .from('categories')
      .insert(category)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Update category
  static async updateCategory(id: string, updates: CategoryUpdate): Promise<Category> {
    // If updating slug, check if it already exists
    if (updates.slug) {
      const existing = await this.getCategoryBySlug(updates.slug)
      if (existing && existing.id !== id) {
        throw new Error('このスラッグは既に使用されています')
      }
    }

    const { data, error } = await supabase
      .from('categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  // Delete category (only if no videos are using it)
  static async deleteCategory(id: string): Promise<void> {
    // First check if any videos are using this category
    const category = await this.getCategory(id)
    if (!category) throw new Error('カテゴリーが見つかりません')

    const { data: videos, error: videoError } = await supabase
      .from('videos')
      .select('id')
      .eq('category', category.slug)
      .limit(1)

    if (videoError) throw videoError

    if (videos && videos.length > 0) {
      throw new Error('このカテゴリーを使用している動画があるため削除できません')
    }

    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id)

    if (error) throw error
  }

  // Update category display order
  static async updateDisplayOrder(categoryOrders: { id: string; display_order: number }[]): Promise<void> {
    const updates = categoryOrders.map(({ id, display_order }) =>
      supabase
        .from('categories')
        .update({ display_order })
        .eq('id', id)
    )

    const results = await Promise.all(updates)
    
    for (const { error } of results) {
      if (error) throw error
    }
  }

  // Toggle category active status
  static async toggleActive(id: string): Promise<Category> {
    const category = await this.getCategory(id)
    if (!category) throw new Error('カテゴリーが見つかりません')

    return this.updateCategory(id, { is_active: !category.is_active })
  }

  // Get categories for video dropdown (active only, formatted for UI)
  static async getCategoriesForDropdown(): Promise<Array<{ id: string; name: string; description?: string }>> {
    const categories = await this.getCategories()
    return [
      { id: 'all', name: 'すべて', description: 'すべてのカテゴリーを表示' },
      ...categories.map(cat => ({ id: cat.slug, name: cat.name, description: cat.description || undefined }))
    ]
  }

  // Initialize default categories (for migration)
  static async initializeDefaultCategories(): Promise<void> {
    const defaultCategories: CategoryInsert[] = [
      {
        name: 'はじめての起業',
        slug: 'beginner',
        description: '起業の基礎を学ぶコンテンツ',
        display_order: 1,
        is_active: true
      },
      {
        name: 'リサーチ・インタビュー',
        slug: 'interview',
        description: 'リサーチとインタビューの手法を学ぶコンテンツ',
        display_order: 2,
        is_active: true
      },
      {
        name: 'アンケート・調査',
        slug: 'survey',
        description: 'アンケートと調査の設計・実施方法を学ぶコンテンツ',
        display_order: 3,
        is_active: true
      },
      {
        name: 'ビジネス戦略',
        slug: 'business',
        description: 'ビジネス戦略の立案と実行を学ぶコンテンツ',
        display_order: 4,
        is_active: true
      },
      {
        name: 'コンテスト・プレゼン',
        slug: 'contest',
        description: 'コンテスト参加とプレゼンテーション技術を学ぶコンテンツ',
        display_order: 5,
        is_active: true
      }
    ]

    for (const category of defaultCategories) {
      try {
        // Check if category already exists
        const existing = await this.getCategoryBySlug(category.slug)
        if (!existing) {
          await this.createCategory(category)
        }
      } catch (error) {
        console.error(`Failed to create category ${category.slug}:`, error)
      }
    }
  }
}